import { ReactNode } from 'react';

// ============================================================
// CARD
// ============================================================
interface CardProps {
    children: ReactNode;
    className?: string;
    hover?: boolean;
    padding?: 'none' | 'sm' | 'md' | 'lg';
}

export function Card({ children, className = '', hover = false, padding = 'md' }: CardProps) {
    const paddingClass = {
        none: '',
        sm: 'p-4',
        md: 'p-5',
        lg: 'p-6',
    }[padding];

    return (
        <div className={`${hover ? 'card-hover' : 'card'} ${paddingClass} ${className}`}>
            {children}
        </div>
    );
}

// ============================================================
// BUTTON
// ============================================================
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'ghost';
    size?: 'sm' | 'md' | 'lg';
    loading?: boolean;
    icon?: ReactNode;
}

export function Button({
    children,
    variant = 'primary',
    size = 'md',
    loading = false,
    icon,
    className = '',
    disabled,
    ...props
}: ButtonProps) {
    const variants = {
        primary: 'btn-primary',
        secondary: 'btn-secondary',
        ghost: 'btn-ghost',
    };

    const sizes = {
        sm: 'btn-sm',
        md: '',
        lg: 'btn-lg',
    };

    return (
        <button
            className={`${variants[variant]} ${sizes[size]} ${className}`}
            disabled={loading || disabled}
            {...props}
        >
            {loading ? (
                <div className="spinner w-4 h-4" />
            ) : icon ? (
                <span className="text-lg">{icon}</span>
            ) : null}
            {children}
        </button>
    );
}

// ============================================================
// INPUT
// ============================================================
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    hint?: string;
}

export function Input({ label, error, hint, className = '', ...props }: InputProps) {
    return (
        <div className="w-full">
            {label && <label className="label">{label}</label>}
            <input
                className={`input ${error ? 'input-error' : ''} ${className}`}
                {...props}
            />
            {hint && !error && <p className="text-xs text-neutral-500 mt-1.5">{hint}</p>}
            {error && <p className="text-xs text-error mt-1.5">{error}</p>}
        </div>
    );
}

// ============================================================
// TEXTAREA
// ============================================================
interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    label?: string;
    error?: string;
}

export function Textarea({ label, error, className = '', ...props }: TextareaProps) {
    return (
        <div className="w-full">
            {label && <label className="label">{label}</label>}
            <textarea
                className={`input min-h-[120px] resize-none ${error ? 'input-error' : ''} ${className}`}
                {...props}
            />
            {error && <p className="text-xs text-error mt-1.5">{error}</p>}
        </div>
    );
}

// ============================================================
// SELECT
// ============================================================
interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
    label?: string;
    error?: string;
    options: { value: string; label: string }[];
    placeholder?: string;
}

export function Select({
    label,
    error,
    options,
    placeholder,
    className = '',
    ...props
}: SelectProps) {
    return (
        <div className="w-full">
            {label && <label className="label">{label}</label>}
            <select
                className={`input ${error ? 'input-error' : ''} ${className}`}
                {...props}
            >
                {placeholder && (
                    <option value="" disabled>
                        {placeholder}
                    </option>
                )}
                {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>
            {error && <p className="text-xs text-error mt-1.5">{error}</p>}
        </div>
    );
}

// ============================================================
// BADGE
// ============================================================
interface BadgeProps {
    children: ReactNode;
    variant?: 'primary' | 'success' | 'warning' | 'error' | 'neutral';
}

export function Badge({ children, variant = 'primary' }: BadgeProps) {
    const variants = {
        primary: 'badge-primary',
        success: 'badge-success',
        warning: 'badge-warning',
        error: 'badge-error',
        neutral: 'badge-neutral',
    };

    return <span className={variants[variant]}>{children}</span>;
}

// ============================================================
// SPINNER
// ============================================================
export function Spinner({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
    const sizes = {
        sm: 'w-4 h-4',
        md: 'w-6 h-6',
        lg: 'w-10 h-10',
    };

    return <div className={`spinner ${sizes[size]}`} />;
}

// ============================================================
// EMPTY STATE
// ============================================================
interface EmptyStateProps {
    icon?: string;
    title: string;
    description?: string;
    action?: ReactNode;
}

export function EmptyState({ icon = '📭', title, description, action }: EmptyStateProps) {
    return (
        <div className="text-center py-12 px-4">
            <div className="text-5xl mb-4">{icon}</div>
            <h3 className="text-lg font-semibold text-neutral-800 mb-1">{title}</h3>
            {description && (
                <p className="text-neutral-500 text-sm mb-4 max-w-xs mx-auto">{description}</p>
            )}
            {action}
        </div>
    );
}

// ============================================================
// STATS CARD
// ============================================================
interface StatsCardProps {
    title: string;
    value: string | number;
    icon?: string;
    trend?: { value: number; label: string };
}

export function StatsCard({ title, value, icon, trend }: StatsCardProps) {
    return (
        <div className="stat-card">
            <div className="flex items-start justify-between">
                <div>
                    <p className="stat-label">{title}</p>
                    <p className="stat-value mt-1">{value}</p>
                    {trend && (
                        <p
                            className={`text-xs font-medium mt-2 ${trend.value >= 0 ? 'text-success' : 'text-error'
                                }`}
                        >
                            {trend.value >= 0 ? '↑' : '↓'} {Math.abs(trend.value)}% {trend.label}
                        </p>
                    )}
                </div>
                {icon && (
                    <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center text-2xl">
                        {icon}
                    </div>
                )}
            </div>
        </div>
    );
}

// ============================================================
// PAGE HEADER
// ============================================================
interface PageHeaderProps {
    title: string;
    subtitle?: string;
    action?: ReactNode;
}

export function PageHeader({ title, subtitle, action }: PageHeaderProps) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
                <h1 className="text-2xl font-bold text-neutral-900">{title}</h1>
                {subtitle && <p className="text-neutral-500 mt-0.5">{subtitle}</p>}
            </div>
            {action}
        </div>
    );
}
